import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import fixedFilterOptions from './fixed-filters';
import ListFilter from './components/ListFilter';
import DateRangeFilter from './components/DateRangeFilter';
import TimeRangeFilter from './components/TimeRangeFilter/TimeRangeFilter.jsx';
import NumberRangeFilter from './components/NumberRangeFilter/NumberRangeFilter.jsx';
import CreditRangeFilter from './components/CreditRangeFilter/CreditRangeFilter.jsx';
import TextFilter from './components/TextFilter/TextFilter.jsx';
import { getFilterPropertyFromFilterId } from './helpers';
import * as actions from './../../../../data/openTimeSearch/actions';
import { openTimeSearchConfig } from './../../../../settings/settings';
import { useLanguage } from './../../../../context/LanguageContext';
import './styles.scss';

const FilterBar = (props) => {
  const { labels } = useLanguage();
  const FILTER_BAR = labels.FILTER_BAR || {};
  const {
    filter,
    filterOptionsFromApi,
    fetchOpenTimeActivities,
    clearOpenTimeActivities,
    sessionData,
    currentBid,
    actionType,
    stations,
    fromAddButton,
    setFromAddButton
  } = props;
  // Init filter options based on visibleFilterOptions and API data
  const visibleFilterOptions = openTimeSearchConfig.filterBar.filterOptions;

  const initialFilterOptions = () => {
    const apiFilters = filterOptionsFromApi.entries.map(opt => ({ ...opt, type: 'list', isUserAdded: false }));
    const allOptions = [...apiFilters, ...fixedFilterOptions];
    return visibleFilterOptions
      .map(id => allOptions.find(opt => opt.id === id))
      .filter(Boolean);
  };

  const [filterOptions, setFilterOptions] = useState(initialFilterOptions);
  const [localFilterPayload, setLocalFilterPayload] = useState(filter);

  useEffect(() => {
    setLocalFilterPayload(filter);
    if (shouldApplyFilter(actionType)) {
      onApplyFilter();
    }
  }, [filter, actionType]);

  const shouldApplyFilter = (actionType) =>
    ['UPDATE_CALENDAR_ACTIVITIES', 'ADD_OPEN_TIME_REQUEST_SUCCESS', 'ADD_OPEN_TIME_REQUEST_FAILURE'].includes(actionType);

  const onApplyFilter = useCallback((e) => {
    if (e) e.preventDefault();
    const persistFilter = true;
    const updatedFilter = {
      ...localFilterPayload,
      firstResult: fromAddButton ? filter.firstResult : 0
    };
    fromAddButton && setFromAddButton(false);
    fetchOpenTimeActivities(sessionData, updatedFilter, persistFilter);
  }, [localFilterPayload, fetchOpenTimeActivities, filter.firstResult, sessionData, fromAddButton]);

  const onClearFilter = useCallback((e) => {
    e.preventDefault();
    const preserved = {
      maxResults: filter.maxResults,
      sortCriteria: filter.sortCriteria,
      sortDirection: filter.sortDirection,
    };
    clearOpenTimeActivities(sessionData, preserved);
  }, [clearOpenTimeActivities, sessionData, filter]);

  const removeFilter = useCallback((filterId) => {
    setFilterOptions(prev => prev.filter(f => f.id !== filterId));
  }, []);

  const updateLocalFilterPayload = useCallback((partialPayload) => {
    setLocalFilterPayload(prev => ({ ...prev, ...partialPayload }));
  }, []);

  const buildFilter = (filterProps) => {
    const filterKey = getFilterPropertyFromFilterId(filterProps.id);
    const commonProps = { key: filterProps.id, removeFilter, updateLocalFilterPayload };
    const payload = localFilterPayload;

    switch (filterProps.type) {
      case 'list':
        return (
          <ListFilter
            {...filterProps}
            {...commonProps}
            selectedItems={payload[filterKey] || []}
            filterKey={filterKey}
            stations={stations || {}}
          />
        );
      case 'date': {
        const start = payload.activityDateRange?.startInclusive;
        const end = payload.activityDateRange?.endInclusive;
        let bidStart = new Date(), bidEnd = new Date();
        if (currentBid?.startDate && currentBid?.endDate) {
          bidStart = new Date(currentBid.startDate[0], currentBid.startDate[1] - 1, currentBid.startDate[2]);
          bidEnd = new Date(currentBid.endDate[0], currentBid.endDate[1] - 1, currentBid.endDate[2]);
        }
        return (
          <DateRangeFilter
            {...filterProps}
            {...commonProps}
            filterKey={filterKey}
            startInclusive={start}
            endInclusive={end}
            startDate={bidStart}
            endDate={bidEnd}
          />
        );
      }
      case 'credit':
        return (
          <CreditRangeFilter
            {...filterProps}
            {...commonProps}
            filterKey={filterKey}
            startInclusive={payload.creditRange?.startInclusive}
            endInclusive={payload.creditRange?.endInclusive}
          />
        );
      case 'text':
        return (
          <TextFilter
            {...filterProps}
            {...commonProps}
            filterKey={filterKey}
            activityName={payload.activityName}
          />
        );
      case 'timeRange':
        return (
          <TimeRangeFilter
            {...filterProps}
            {...commonProps}
            filterKey={filterKey}
            startInclusive={payload?.[filterKey]?.startInclusive}
            endInclusive={payload?.[filterKey]?.endInclusive}
          />
        );
      case 'numberRange':
        return (
          <NumberRangeFilter
            {...filterProps}
            {...commonProps}
            filterKey={filterKey}
            startInclusive={payload?.[filterKey]?.startInclusive}
            endInclusive={payload?.[filterKey]?.endInclusive}
          />
        );
      default:
        return <span key={filterProps.id} />;
    }
  };

  return (
    <div className="filter-bar">
      <div className="buttons-wrapper">
        {filterOptions.map(buildFilter)}
      </div>
      <div className="actions-wrapper">
        <span onClick={onApplyFilter} className="filter-button-actions">{FILTER_BAR.APPLY_BTN}</span>
        <span onClick={onClearFilter} className="filter-button-actions cancel">{FILTER_BAR.CLEAR_BTN}</span>
      </div>
    </div>
  );
};

FilterBar.propTypes = {
  clearOpenTimeActivities: PropTypes.func,
  fetchOpenTimeActivities: PropTypes.func,
  filter: PropTypes.object.isRequired,
  filterOptionsFromApi: PropTypes.object.isRequired,
  sessionData: PropTypes.object.isRequired,
  currentBid: PropTypes.object.isRequired,
  actionType: PropTypes.string.isRequired,
  stations: PropTypes.object,
  fromAddButton: PropTypes.bool.isRequired,
  setFromAddButton: PropTypes.func.isRequired,
  fromAddButton: PropTypes.bool.isRequired
};

FilterBar.defaultProps = {
  clearOpenTimeActivities: () => {},
  fetchOpenTimeActivities: () => {},
  setFromAddButton: () => {},
  fromAddButton: false,
};

const mapStateToProps = (state) => ({
  filter: state.openTimeSearch.filter,
  filterOptionsFromApi: state.bidSession.bidSession.filters,
  currentBid: state.bidSession.bidSession.currentBid,
  stations: state.openTimeSearch.stations,
  
});

const mapDispatchToProps = (dispatch) => ({
  clearOpenTimeActivities: (sessionData, maxResults) =>
    dispatch(actions.clearOpenTimeActivities(sessionData, maxResults)),
  fetchOpenTimeActivities: (sessionData, filterPayload, persistFilter) =>
    dispatch(actions.fetchOpenTimeActivities(sessionData, filterPayload, persistFilter)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FilterBar);
